// What's still on offer, over the panes (spec "Asking until it's done or declined"; the Mac's OffersBanner):
// bringing a computer's setup over, then a coding agent finishing what setup left. Each takes yes, or, behind
// its ⋯, Not now (back on the next visit) or Don't ask again (every app stops asking). The same blue card as the
// Mac's (.ws-banner[data-tone='offer']); under 680 wide the Finish buttons fold into one, the agents behind it.

import { IconChecklist, IconChevronDown, IconDots, IconEyeOff, IconWand, IconX } from '@tabler/icons-react';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { declineMagicSetup, hideForVisit, setupOpen, startFinish, useFinish, useNotNow, type Finish } from '../pages/GetStarted';
import { navigate } from '../router';
import { conn } from '../net/connection';
import { errMsg, toast, useStore } from '../store';
import { AgentMark } from './AgentMark';
import { Button, Ic, Tooltip, presence } from './ds';
import { BannerSlot } from './BannerSlot';
import { useMenu } from './menu';

type Agent = Finish['agents'][number];
type Item = { key: string; label: string; mark: ReactNode; onSelect: () => void; testid?: string };

/** Narrow: what's left in a few words, so it stays on one line. Items that share a verb count up ("Install 3
    items"), one alone keeps its words; two groups at most, then how many more. The Mac's OffersBanner.summary. */
function summary(left: string[], todo: number): string {
  const groups = new Map<string, string[]>();
  for (const item of left) {
    const verb = item.split(' ')[0];
    groups.set(verb, [...(groups.get(verb) ?? []), item]);
  }
  const shown = [...groups].slice(0, 2);
  const words = shown.map(([verb, items]) => (items.length === 1 ? items[0] : `${verb} ${items.length} items`));
  const rest = todo - shown.reduce((n, [, items]) => n + items.length, 0);
  return words.join(' · ') + (rest > 0 ? ` and ${rest} more` : '');
}

/** A button that opens a short menu under it, its right edges on the button's. */
function MenuButton({ trigger, items, label, tip, width }: { trigger: (open: boolean, toggle: () => void) => ReactNode; items: Item[]; label: string; tip?: string; width?: number }) {
  const m = useMenu('.ws-main', 0);
  const btn = trigger(m.open, () => m.setOpen((o) => !o));
  return (
    <span className="ws-menu-wrap" ref={m.ref}>
      {tip && !m.open ? <Tooltip label={tip} align="end">{btn}</Tooltip> : btn}
      {m.p.mounted && (
        <div className="ws-menu ds-float" role="menu" aria-label={label} style={width ? { width } : undefined} {...presence(m.p)}>
          {items.map((it) => (
            <button
              key={it.key}
              type="button"
              className="ws-menu-item"
              role="menuitem"
              data-testid={it.testid}
              onClick={() => {
                m.setOpen(false);
                it.onSelect();
              }}
            >
              <span className="ws-menu-icon">{it.mark}</span>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function Row({ icon, title, detail, id, onNotNow, onNever, children }: { icon: typeof IconWand; title: string; detail: string; id: string; onNotNow: () => void; onNever: () => void; children: ReactNode }) {
  return (
    <div className="ws-banner" data-size="lg" data-tone="offer" role="status" data-testid={id}>
      <span className="ws-banner-icon">
        <Ic icon={icon} />
      </span>
      <span className="ws-notice-text">
        <span className="ws-notice-title">{title}</span>
        <span className="ws-notice-body">{detail}</span>
      </span>
      <span className="ws-banner-actions">
        {children}
        {/* Not now and Don't ask again together behind one control: the offer keeps its one or two things to do in plain sight */}
        <MenuButton
          label="More"
          tip="Not now, or don’t ask again"
          items={[
            { key: 'later', label: 'Not now', mark: <Ic icon={IconX} size={16} />, onSelect: onNotNow, testid: `${id}-not-now` },
            { key: 'never', label: 'Don’t ask again', mark: <Ic icon={IconEyeOff} size={16} />, onSelect: onNever, testid: `${id}-never` },
          ]}
          trigger={(open, toggle) => (
            <Button variant="icon" size="sm" radius={6} aria-label="More" aria-haspopup="menu" aria-expanded={open} data-testid={`${id}-more`} onClick={toggle}>
              <Ic icon={IconDots} />
            </Button>
          )}
        />
      </span>
    </div>
  );
}

/** `demo`: show this finish offer as it is (DsLab), without asking the machine. */
export function OffersBanner({ demo }: { demo?: Finish } = {}) {
  const me = useStore((s) => s.me);
  const ready = useStore((s) => s.me?.machine?.status === 'running');
  const setupLater = useNotNow('setup');
  const finishLater = useNotNow('finish');
  const [asked, reloadFinish] = useFinish(ready && !demo);
  const finish = demo ?? asked;
  const [starting, setStarting] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setNarrow(e.contentRect.width < 680));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const askSetup = !demo && !!me && setupOpen(me) && !setupLater;
  const best = finish?.agents[0];
  const askFinish = !askSetup && !!finish?.ask && !!best && (!!demo || !finishLater);

  const start = (a: Agent) => {
    setStarting(true);
    void startFinish(finish!, a).finally(() => setStarting(false));
  };
  const declineFinish = async () => {
    try {
      await conn.control('setup.finish.decline');
      reloadFinish();
    } catch (e) {
      toast({ title: 'Couldn’t save that', body: errMsg(e), kind: 'error' });
    }
  };
  const agentItem = (a: Agent, label: string): Item => ({ key: a.id, label, mark: <AgentMark id={a.id} name={a.name} />, onSelect: () => start(a), testid: `offer-finish-${a.id}` });

  let body: ReactNode = null;
  if (askSetup) {
    body = (
      <Row
        icon={IconWand}
        id="offer-setup"
        title="Bring your computer’s setup"
        detail="Tools, shell, coding agents, sign-ins and projects move over, so your cloud machine feels like your own."
        onNotNow={() => hideForVisit('setup')}
        onNever={() => void declineMagicSetup()}
      >
        <Button size="sm" variant="primary" onClick={() => navigate('/dashboard')} data-testid="offer-setup-go">
          Get started
        </Button>
      </Row>
    );
  } else if (askFinish && finish && best) {
    const more = finish.todo > 3 ? ` and ${finish.todo - 3} more` : '';
    body = (
      <Row
        icon={IconChecklist}
        id="offer-finish"
        title={finish.todo === 1 ? '1 thing left from setup' : `${finish.todo} things left from setup`}
        detail={(narrow ? summary(finish.left, finish.todo) : finish.left.slice(0, 3).join(' · ') + more) + (best.signIn ? `. ${best.name} asks you to sign in first.` : '')}
        onNotNow={() => hideForVisit('finish')}
        onNever={() => void declineFinish()}
      >
        {narrow && finish.agents.length > 1 ? (
          // no room for both: one button, the best agent's mark, Finish and a chevron; it opens the agents (the best
          // first), so starting takes one more click but nothing is cut
          <MenuButton
            label="Finish with a coding agent"
            width={224}
            items={finish.agents.map((a) => agentItem(a, `Finish with ${a.name}`))}
            trigger={(open, toggle) => (
              <Button size="sm" variant="primary" disabled={starting} aria-label="Finish with a coding agent" aria-haspopup="menu" aria-expanded={open} data-testid="offer-finish-pick" onClick={toggle}>
                <AgentMark id={best.id} name={best.name} />
                Finish
                <Ic icon={IconChevronDown} size={12} />
              </Button>
            )}
          />
        ) : (
          <>
            <Button size="sm" variant="primary" disabled={starting} aria-label={`Finish with ${best.name}`} data-testid="offer-finish-go" onClick={() => start(best)}>
              <AgentMark id={best.id} name={best.name} />
              {narrow ? 'Finish' : `Finish with ${best.name}`}
            </Button>
            {!narrow && finish.agents.length > 1 && (
              <MenuButton
                label="Another agent"
                items={finish.agents.slice(1).map((a) => agentItem(a, a.name))}
                trigger={(open, toggle) => (
                  <Button size="sm" disabled={starting} aria-haspopup="menu" aria-expanded={open} data-testid="offer-finish-other" onClick={toggle}>
                    Another agent
                    <Ic icon={IconChevronDown} size={14} />
                  </Button>
                )}
              />
            )}
          </>
        )}
      </Row>
    );
  }
  return (
    <div className="ws-offers" ref={box}>
      <BannerSlot open={body !== null}>{body}</BannerSlot>
    </div>
  );
}
